import { GenericAbortSignal } from 'axios';

import {
  ImportConfiguration,
  ImportConfigurationOptions,
  TypedImportConfiguration,
  TypedImportConfigurationIndex,
} from 'models/flexible/ImportConfiguration';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminImportConfigurationCreateParams,
  AdminImportConfigurationIndexInclude,
  AdminImportConfigurationShowInclude,
  AdminImportConfigurationUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminImportConfigurationsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminImportConfigurations', params] as const,
  show: (id: string) => ['adminImportConfigurations', id] as const,
};

const URL = 'admin/import_configurations';

export type AdminImportConfigurationIndexParams = BaseIndexQueryParams & {
  clientType?: string;
};

export async function adminIndexImportConfigurations<I extends AdminImportConfigurationIndexInclude = never>(
  params: AdminImportConfigurationIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<TypedImportConfigurationIndex<I>>> {
  return http
    .get<
      Paginated<ImportConfigurationOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new ImportConfiguration(x)),
    })) as any as Promise<Paginated<TypedImportConfigurationIndex<I>>>;
}

// ---------------------------------------------------------------------------
// Typed show — returns AdminImportConfiguration with include-dependent fields
// ---------------------------------------------------------------------------

export function adminShowImportConfiguration<I extends AdminImportConfigurationShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<TypedImportConfiguration<I>> {
  return http
    .get(`${URL}/${id}`, {
      params: { include: includes?.join(',') },
      signal,
    })
    .then((res) => new ImportConfiguration(res.data)) as Promise<TypedImportConfiguration<I>>;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function adminCreateImportConfiguration(data: AdminImportConfigurationCreateParams) {
  return http.post<ImportConfigurationOptions>(URL, data).then((res) => new ImportConfiguration(res.data));
}

export async function adminUpdateImportConfiguration(id: string, data: AdminImportConfigurationUpdateParams) {
  return http.put<ImportConfigurationOptions>(`${URL}/${id}`, data).then((res) => new ImportConfiguration(res.data));
}
